/* Rapporter · Service · «Spør HydroHub» on the overview · the AI workspace. Same rules as views.js. */
import { ChevronRight, FileSpreadsheet, FileText, Maximize2, MessageSquarePlus, Pin, Plus, Search, Wrench } from "lucide-react";
import { DailyEnergyChart } from "@/components/charts/SimpleCharts";
import { MessageBlock } from "@/components/ai/Thread";
import { Composer } from "@/components/ai/Composer";
import { LensMark } from "@/components/brand/ProximaLogo";
import { fmtDate, fmtInt, fmtNOK, fmtNum, fmtPct, fmtPrice } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AI_HISTORY, AI_MESSAGES, REPORT, SERVICE_RECORDS, SERVICE_REQUESTS } from "@/data/tilbod/hhDemo";
import { Btn, DeltaText, Figure, LabelCaps, Meta, N, PageTitle, Pill, Segmented, SelectStub, TextLink } from "./ui";
import { Oversikt } from "./views";

const noop = () => {};

/* ---------- Rapporter ---------- */
export const Rapporter = ({ mode }) => {
  const narrow = mode === "narrow";
  const r = REPORT;
  const cols = mode === "full" ? ["Installert", "Produksjon", "Kap.faktor", "Tilgj.", "Stanset", "Snittpris", "Est. inntekt"] : mode === "compact" ? ["Installert", "Produksjon", "Kap.faktor", "Tilgj.", "Est. inntekt"] : ["Produksjon", "Est. inntekt"];
  const cell = (row, c) =>
    ({
      Installert: fmtNum(row.capacity_mw, 1) + " MW",
      Produksjon: fmtInt(row.energy_mwh) + " MWh",
      "Kap.faktor": fmtPct(row.capacity_factor_pct, 0),
      "Tilgj.": fmtPct(row.availability_pct, 1),
      Stanset: `${row.hours_stopped} t`,
      Snittpris: fmtPrice(row.avg_price_nok_mwh),
      "Est. inntekt": fmtNOK(row.revenue_nok),
    })[c];
  return (
    <div className={narrow ? "space-y-6" : "space-y-8"}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <PageTitle>Rapporter</PageTitle>
          <Meta>
            {r.period_label} · <N>26</N> kraftverk · dagsverdier
          </Meta>
        </div>
        {!narrow ? (
          <div className="flex items-center gap-2">
            <Btn variant="outline" icon={FileText}>CSV</Btn>
            <Btn variant="outline" icon={FileSpreadsheet}>Excel</Btn>
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-4">
        <Segmented items={narrow ? ["Måned", "30 d", "Hittil i år"] : ["Forrige måned", "Siste 30 dager", "Hittil i år", "Egendefinert"]} value={narrow ? "Måned" : "Forrige måned"} />
        <SelectStub className={narrow ? "" : "ml-auto"}>Alle kraftverk</SelectStub>
      </div>
      <div className={cn("grid gap-x-8 gap-y-6", narrow ? "grid-cols-2" : "grid-cols-4")}>
        <Figure label="Produksjon" value={fmtInt(r.energy_mwh)} unit="MWh" sub={<span className="inline-flex items-baseline gap-1.5"><DeltaText value={8.6} /> mot august</span>} />
        <Figure label="Est. inntekt" value="29,4 MNOK" sub={<>Snittpris <N>{fmtPrice(r.avg_spot_nok_mwh)}</N></>} />
        <Figure label="Kapasitetsfaktor" value={fmtNum(r.capacity_factor_pct, 0)} unit="%" sub="Mot installert effekt, hele perioden" />
        <Figure label="Timer stanset" value={fmtInt(r.hours_stopped)} unit="t" sub={<>Tilgjengelighet <N>{fmtPct(r.availability_pct, 1)}</N></>} />
      </div>
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <LabelCaps>Produksjon per døgn</LabelCaps>
          <span className="text-[13px] text-muted-foreground">MWh</span>
        </div>
        <div className="mt-3">
          <DailyEnergyChart rows={r.per_day} height={mode === "full" ? 260 : mode === "compact" ? 210 : 190} />
        </div>
      </div>
      <div>
        <LabelCaps>Per kraftverk</LabelCaps>
        <table className="mt-3 w-full text-[14.5px]">
          <thead>
            <tr className="border-b border-border text-left text-[13px] text-muted-foreground">
              <th className="py-2.5 pr-4 font-medium">Kraftverk</th>
              {cols.map((c) => (
                <th key={c} className="py-2.5 pr-4 text-right font-medium last:pr-0">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.rows.map((row) => (
              <tr key={row.name} className="border-b border-border">
                <td className="py-3 pr-4 align-middle">
                  <span className="text-[15px] font-semibold">{row.name.replace(/ kraftverk$/, "")}</span>
                </td>
                {cols.map((c) => (
                  <td key={c} className={cn("metric py-3 pr-4 text-right align-middle last:pr-0", c === "Produksjon" || c === "Est. inntekt" ? "text-foreground" : "text-muted-foreground")}>
                    {cell(row, c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- Service ---------- */
export const Service = ({ mode }) => {
  const narrow = mode === "narrow";
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <PageTitle>Service</PageTitle>
          <Meta>
            <N className="font-medium text-foreground">3</N> åpne henvendelser · siste service <N>2. okt. 2026</N>
          </Meta>
        </div>
        <Btn variant="primary" icon={MessageSquarePlus}>Ny henvendelse</Btn>
      </div>
      <div className="flex items-baseline justify-between gap-3 border-b border-border pb-3">
        <LabelCaps>
          Henvendelser <span className="ml-1.5 text-foreground">· 3 åpne</span>
        </LabelCaps>
        <span className="text-[13px] text-muted-foreground">Sortert etter sist oppdatert</span>
      </div>
      <ul className="divide-y divide-border">
        {SERVICE_REQUESTS.map((q) => (
          <li key={q.id} className="flex items-start justify-between gap-3 py-3 first:pt-0">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-[15px] font-medium">{q.subject}</span>
                <Pill status={q.status} />
              </div>
              <div className="metric mt-1 text-[12.5px] text-muted-foreground">
                {q.ref} · {q.category_label} · {q.plant_name} · {q.when}
                {q.sub ? ` · ${q.sub}` : ""}
              </div>
            </div>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden="true" />
          </li>
        ))}
      </ul>
      {!narrow ? (
        <div className="border-t border-border pt-6">
          <div className="flex items-baseline justify-between gap-3">
            <LabelCaps>
              Servicehistorikk <span className="ml-1.5 text-foreground">· {SERVICE_RECORDS.length}</span>
            </LabelCaps>
            <TextLink>Alle rapporter</TextLink>
          </div>
          <table className="mt-3 w-full text-[14px]">
            <thead>
              <tr className="border-b border-border text-left text-[13px] text-muted-foreground">
                <th className="py-2.5 pr-4 font-medium">Dato</th>
                <th className="py-2.5 pr-4 font-medium">Kraftverk</th>
                <th className="py-2.5 pr-4 font-medium">Type</th>
                {mode === "full" ? <th className="py-2.5 pr-4 font-medium">Beskrivelse</th> : null}
                <th className="py-2.5 pr-4 font-medium">Tekniker</th>
                <th className="py-2.5 pr-4 text-right font-medium">Timer</th>
                <th className="py-2.5 text-right font-medium">Rapport</th>
              </tr>
            </thead>
            <tbody>
              {SERVICE_RECORDS.map((rec) => (
                <tr key={rec.id} className="border-b border-border">
                  <td className="metric whitespace-nowrap py-3 pr-4 text-[13.5px]">{fmtDate(rec.date)}</td>
                  <td className="py-3 pr-4 font-medium">{rec.plant.replace(/ kraftverk$/, "")}</td>
                  <td className="py-3 pr-4">
                    <span className="inline-flex items-center gap-1.5">
                      <Wrench className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> {rec.type}
                    </span>
                  </td>
                  {mode === "full" ? <td className="max-w-md py-3 pr-4 text-[13.5px] text-muted-foreground">{rec.description}</td> : null}
                  <td className="py-3 pr-4 text-[13.5px]">{rec.technician}</td>
                  <td className="metric py-3 pr-4 text-right">{fmtNum(rec.hours, rec.hours % 1 ? 1 : 0)}</td>
                  <td className="metric py-3 text-right text-[13px] text-muted-foreground">{rec.report_ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

/* ---------- «Spør HydroHub»: the panel over the overview ---------- */
const AskSheet = ({ mode }) => {
  const width = mode === "full" ? 560 : mode === "compact" ? 480 : "100%";
  return (
    <>
      <div className="hh-sheet-backdrop" />
      <div className="hh-sheet" style={{ width }}>
        <div className="flex items-center gap-3 border-b border-border px-5 py-3.5">
          <LensMark className="h-5 w-5" />
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold">Spør HydroHub</div>
            <div className="truncate text-[12.5px] text-muted-foreground">Haugeland kraftverk · svar bygger på målinger og hendelser i HydroHub</div>
          </div>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground"><Plus className="h-4 w-4" aria-hidden="true" /></span>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground"><Maximize2 className="h-4 w-4" aria-hidden="true" /></span>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden px-5">
          {AI_MESSAGES.map((m) => (
            <MessageBlock key={m.id} message={m} compact />
          ))}
        </div>
        <div className="border-t border-border bg-background p-3">
          <Composer compact onSend={noop} onStop={noop} sending={false} contextLabel="Haugeland kraftverk" onClearContext={noop} />
        </div>
      </div>
    </>
  );
};
export const Spor = ({ mode }) => <Oversikt mode={mode} />;
export const sporOverlay = (mode) => <AskSheet mode={mode} />;

/* ---------- the AI workspace (/ai) ---------- */
const HistoryRail = () => (
  <div className="flex h-full w-[264px] shrink-0 flex-col border-r border-border bg-[hsl(var(--rail))]">
    <div className="space-y-2 p-3">
      <span className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-input bg-background text-[13.5px] font-medium">
        <Plus className="h-4 w-4" aria-hidden="true" /> Ny samtale
      </span>
      <span className="flex h-9 items-center gap-2 rounded-md bg-rail-hover/70 px-2.5 text-[13.5px] text-muted-foreground">
        <Search className="h-4 w-4" aria-hidden="true" /> Søk i samtaler
      </span>
    </div>
    <div className="min-h-0 flex-1 space-y-4 px-3 pb-3">
      {AI_HISTORY.map((g) => (
        <div key={g.group}>
          <div className="px-2 pb-1 text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{g.group}</div>
          <ul className="space-y-0.5">
            {g.items.map((it) => (
              <li key={it.title} className={cn("flex h-8 items-center gap-2 rounded-md px-2 text-[13.5px]", it.active ? "bg-card font-medium text-foreground shadow-[0_1px_2px_hsl(var(--brand-ink)/0.08)]" : "text-foreground/80")}>
                <span className="truncate">{it.title}</span>
                {it.active ? <Pin className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

export const Ai = ({ mode }) => {
  const narrow = mode === "narrow";
  return (
    <div className="flex h-full min-h-0 w-full">
      {mode === "full" ? <HistoryRail /> : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className={cn("flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border", narrow ? "px-4" : "px-6")}>
          <div className="flex min-w-0 items-center gap-3">
            <span className="text-[15px] font-semibold">Spør HydroHub</span>
            {!narrow ? <SelectStub className="h-8">Haugeland kraftverk</SelectStub> : null}
          </div>
          <span className="whitespace-nowrap text-[12px] text-muted-foreground">Lesetilgang · fiktive kundedata</span>
        </div>
        <div className={cn("min-h-0 flex-1 overflow-hidden", narrow ? "px-4" : "px-6")}>
          <div className="mx-auto max-w-[760px] pt-2">
            {AI_MESSAGES.map((m) => (
              <MessageBlock key={m.id} message={m} compact={narrow} />
            ))}
          </div>
        </div>
        <div className={cn("shrink-0 border-t border-border bg-background", narrow ? "p-3" : "px-6 py-3")}>
          <div className="mx-auto max-w-[760px]">
            <Composer onSend={noop} onStop={noop} sending={false} contextLabel="Haugeland kraftverk" onClearContext={noop} />
            {!narrow ? <p className="mt-2 text-center text-[12px] text-muted-foreground">Svar bygger på målinger og hendelser i HydroHub. Kontroller viktige tall mot kilden.</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

